// MobileUnlock — paywall screen shown to unpaid users inside the Android app.
//
// On Android (Capacitor): drives Google Play Billing for the $0.99 lifetime
// `opencsv_lifetime` product. On approval, the purchase token is verified
// server-side; once verified, the user's `is_paid` flips to true and we route
// straight into the editor.
//
// On web/iOS or if billing fails to init: falls back to "Pay on the web"
// (opens opencsv.io/pricing in the system browser).

import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Lock, ExternalLink, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
    initBilling,
    onBillingEvent,
    getLifetimeProduct,
    buyLifetime,
    restorePurchases,
    isAndroidPlatform,
} from "../lib/googlePlayBilling";

export default function MobileUnlock() {
    const { user, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const onAndroid = isAndroidPlatform();

    const [billingReady, setBillingReady] = useState(false);
    const [billingError, setBillingError] = useState(null);
    const [product, setProduct] = useState(null);
    const [busy, setBusy] = useState(false); // purchase in progress
    const [restoring, setRestoring] = useState(false);

    // Initialize the store on mount (Android only).
    useEffect(() => {
        if (!onAndroid) return;
        let cancelled = false;
        initBilling()
            .then(() => {
                if (cancelled) return;
                setBillingReady(true);
                const snap = getLifetimeProduct();
                if (snap) setProduct(snap);
            })
            .catch((e) => {
                if (cancelled) return;
                setBillingError(e?.message || "Billing failed to initialise");
            });
        return () => { cancelled = true; };
    }, [onAndroid]);

    // Listen for purchase lifecycle events.
    useEffect(() => {
        if (!onAndroid) return undefined;
        const unsub = onBillingEvent(async (evt) => {
            if (evt.type === "product" && evt.product?.id === "opencsv_lifetime") {
                setProduct(evt.product);
            } else if (evt.type === "approved") {
                setBusy(true); // server verification in flight
            } else if (evt.type === "verified") {
                setBusy(false);
                toast.success("Lifetime access unlocked!");
                if (refresh) await refresh();
                navigate("/app", { replace: true });
            } else if (evt.type === "error") {
                setBusy(false);
                setRestoring(false);
                const msg = evt.error?.message || String(evt.error || "Purchase error");
                // Filter user-cancelled noise.
                if (!/cancel/i.test(msg)) toast.error(msg);
            }
        });
        return unsub;
    }, [onAndroid, navigate, refresh]);

    const handleBuy = useCallback(async () => {
        try {
            setBusy(true);
            await buyLifetime();
            // success path continues via the "verified" billing event.
        } catch (e) {
            setBusy(false);
            toast.error(e?.message || "Could not start checkout");
        }
    }, []);

    const handleRestore = useCallback(async () => {
        try {
            setRestoring(true);
            await restorePurchases();
            toast.info("Checking Play for previous purchases…");
            // If anything is found, the plugin replays an `approved` event,
            // which then runs through verifyWithBackendAndFinish.
        } catch (e) {
            toast.error(e?.message || "Restore failed");
        } finally {
            setRestoring(false);
        }
    }, []);

    const openWebPay = async () => {
        const url = "https://opencsv.io/pricing";
        try {
            const w = window.open(url, "_system");
            if (!w) window.location.href = url;
        } catch {
            window.location.href = url;
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const priceLabel = product?.priceString || "$0.99";
    const showNativeButton = onAndroid && billingReady && !billingError;

    return (
        <>
            <Helmet><title>Unlock OpenCSV</title></Helmet>
            <section className="min-h-[80vh] flex items-center bg-paper">
                <div className="mx-auto max-w-md w-full px-6 py-10 text-center" data-testid="mobile-unlock">
                    <div className="inline-flex items-center justify-center w-16 h-16 border border-cobalt bg-white mb-6">
                        <Lock size={28} className="text-cobalt" />
                    </div>
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ unlock</div>
                    <h1 className="mt-2 font-display font-black tracking-tighter text-4xl">
                        One tap. Yours forever.
                    </h1>
                    <p className="mt-4 text-sm text-ink-soft">
                        OpenCSV is <span className="font-semibold text-ink">{priceLabel} once</span>.
                        No subscriptions. No ads. Same account works on web + every device.
                    </p>

                    {/* Android: native Google Play Billing */}
                    {showNativeButton && (
                        <>
                            <button
                                onClick={handleBuy}
                                disabled={busy}
                                className="btn-brutal mt-8 w-full bg-cobalt text-white px-5 py-3.5 ui-heading font-semibold border border-cobalt inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid="mobile-unlock-buy-button"
                            >
                                {busy ? (
                                    <><Loader2 size={14} className="animate-spin" /> Working…</>
                                ) : (
                                    <>Unlock for {priceLabel}</>
                                )}
                            </button>
                            <button
                                onClick={handleRestore}
                                disabled={restoring || busy}
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs ui-heading uppercase tracking-widest text-ink-muted hover:text-cobalt disabled:opacity-50"
                                data-testid="mobile-unlock-restore-button"
                            >
                                <RefreshCw size={12} className={restoring ? "animate-spin" : ""} />
                                {restoring ? "Restoring…" : "Restore previous purchase"}
                            </button>
                        </>
                    )}

                    {/* Android: billing init still in flight */}
                    {onAndroid && !billingReady && !billingError && (
                        <div className="mt-8 inline-flex items-center gap-2 text-sm text-ink-muted" data-testid="mobile-unlock-loading">
                            <Loader2 size={14} className="animate-spin" /> Connecting to Google Play…
                        </div>
                    )}

                    {/* Android: billing init failed → web fallback */}
                    {onAndroid && billingError && (
                        <>
                            <div className="mt-6 text-xs text-signal-red" data-testid="mobile-unlock-billing-error">
                                Couldn't reach Google Play: {billingError}
                            </div>
                            <button
                                onClick={openWebPay}
                                className="btn-brutal mt-3 w-full bg-cobalt text-white px-5 py-3.5 ui-heading font-semibold border border-cobalt inline-flex items-center justify-center gap-2"
                                data-testid="mobile-unlock-pay-button"
                            >
                                Pay on the web — $1 <ExternalLink size={14} />
                            </button>
                        </>
                    )}

                    {/* Web / iOS / non-native fallback */}
                    {!onAndroid && (
                        <button
                            onClick={openWebPay}
                            className="btn-brutal mt-8 w-full bg-cobalt text-white px-5 py-3.5 ui-heading font-semibold border border-cobalt inline-flex items-center justify-center gap-2"
                            data-testid="mobile-unlock-pay-button"
                        >
                            Pay on the web — $1 <ExternalLink size={14} />
                        </button>
                    )}

                    <p className="mt-3 text-[11px] text-ink-muted">
                        Already paid? Sign in with the same email — your unlock follows you.
                    </p>

                    <div className="mt-10 pt-6 border-t border-ink/10 text-xs text-ink-muted space-y-1">
                        <div>Signed in as <span className="font-mono">{user?.email}</span></div>
                        <button onClick={handleLogout} className="underline" data-testid="mobile-unlock-signout">
                            Sign out
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
}
